// @flow
/* global platform */
import React from 'react';
import RestApiUtil from './RestApiUtil';
import {setExpCampaignAndVariation, isTestAExperience, isTestBExperience} from './TestUtil';
import {FFCLocaleMap, DownloadWorkflow, campaignId} from '../constants/appsConstants';
import CompatibilityUtil from './CompatibilityUtil';
import DeviceDetectionUtil from './DeviceDetectionUtil';
import ThorConnectionUtil from './ThorConnectionUtil';
import AnalyticsUtil, {getComName, getSvcName} from './AnalyticsUtil';
import AppsAnalytics from '../analytics/AppsAnalytics';
import CSSUtil from './CSSUtil';
import AppsUtil from './AppsUtil';
import getConfigApps from '../config';
import Url from '../../src/utils/Url';
import AppPickerUtil from './AppPicker/AppPickerUtil';
import uuid from 'uuid';
import {campaignUrlUriMapping} from '../constants/ccdConstants';
import {getCookie} from './cookie';
import wamClient from '@messaging/bifrost';

type DownloadProduct = {
	sapcode: string,
	checkCompatability?: boolean,
	hasThor: ?boolean,
	ffcAllVersionData?: ?Object,
	downloadDelay?: number,
	downloadCoachMark?: React.Element,
	isAllApps?: boolean,
	passPhraseFailureCb?: Function
};

class DownloadUtil {
	/*
		sapcode : Sapcode of app to download
		checkCompatability : Check whether app(or any older version) is compatible before downloading
							 If set to false, downloading will be initiated assuming some version is compatible
							 and function will resolve to Kaizen/Thor
		hasThor : Thor check, if value is not set the function will perform a check for thor
		ffcAllVersionData : Pass as argument if all version data for app was pre-fetched from ffc
		downloadCoachMark : React component to display on download page during kaizen workflow
		isAllApps : If set as true, download for app will be initated via thor by first downloading thor onto user's system
		passPhraseFailureCb : Passphrase failure callback for thor workflow
		downloadDelay : Delay after which you want the download to start. Promise will resolve without waiting for delay.
	*/
	downloadProduct({sapcode = 'KCCC', checkCompatability = true, hasThor = undefined, ffcAllVersionData = undefined, downloadDelay = 0, downloadCoachMark = undefined, isAllApps = false, passPhraseFailureCb = undefined}: DownloadProduct = {}): Promise<string> {
		return new Promise(async(resolve) => {
			// ideally the function should not be called for mobile devices
			const dataFetched = DeviceDetectionUtil.isMobileOrTab()
				? false
				: await this.setFFCAllVersionData(sapcode, ffcAllVersionData, isAllApps);
			if (!dataFetched) {
				return resolve(DownloadWorkflow.ErrorFFCFetchFail);
			}
			const productData = AppsUtil.appsFFCProductList && AppsUtil.appsFFCProductList[sapcode];
			if (!productData) {
				return resolve(DownloadWorkflow.ErrorFFCDataEmpty);
			}
			let isCompatible = !checkCompatability;
			let compatibleVersionIsOlder = false;
			if (checkCompatability) {
				isCompatible = CompatibilityUtil.isAppSupported(sapcode);
				if (!isCompatible) {
					compatibleVersionIsOlder = !CompatibilityUtil.isOSDeprecated()
												&& CompatibilityUtil.isAnyOlderVersionSupported(sapcode);
					isCompatible = compatibleVersionIsOlder;
				}
			}
			if (!isCompatible) {
				return resolve(DownloadWorkflow.Incompatible);
			}

			const isThorPresent = await this.getThorStatus(hasThor);

			setTimeout(() => this.startDownload(productData, isThorPresent, isAllApps, passPhraseFailureCb, downloadCoachMark), downloadDelay);
			if (isThorPresent) {
				return compatibleVersionIsOlder ? resolve(DownloadWorkflow.ThorOldVersion) : resolve(DownloadWorkflow.Thor);
			}
			return compatibleVersionIsOlder ? resolve(DownloadWorkflow.KaizenOldVersion) : resolve(DownloadWorkflow.Kaizen);
		});
	}

	async setFFCAllVersionData(sapcode: string, ffcAllVersionData: Object, isDownloadViaCCD: boolean): Promise<boolean> | boolean {
		const promiseArr = [true, true, true]; // If data is already fetched simply resolve the respective promise with value true.

		// Step - 1.1 - Check if the allVersionsFFCProductList and appsFFCProductList variables have the data for the given sapcode.
		if (!AppsUtil.allVersionsFFCProductList[sapcode] || !(AppsUtil.appsFFCProductList && AppsUtil.appsFFCProductList[sapcode])) {
			let dataFetchPromise = Promise.resolve(false);
			if (AppsUtil.allVersionsFFCProductList[sapcode]) {
				// Step - 1.2 - If allVersionsFFCProductList has the data for the given sapcode, this is sufficient we can resolve the promise as true
				dataFetchPromise = Promise.resolve(true);
			}
			else if	(ffcAllVersionData) {
				// Step - 1.2 - If not, check ffcAllVersionData input variable and store this data and resolve the promise to true
				AppsUtil.processAndStoreProductFFCData(ffcAllVersionData, sapcode);
				dataFetchPromise = Promise.resolve(true);
			}
			// Step - 1.3 - If even ffcAllVersionData input variable doesn't have data, fetch the data and store it
			else if (!AppsUtil.isFFCFallback) {
				dataFetchPromise = RestApiUtil.fetchFFCAllVersionData(sapcode);
			}
			else {
				dataFetchPromise = RestApiUtil.fetchFFCAllVersionDataFallback(sapcode);
			}
			promiseArr[0] = dataFetchPromise.then((dataFetched) => {
				/**
				 * Step - 1.4 - Now we have all the data for the given sapcode, so save it inside appsFFCProductList if not already present in it
				 * At the end of the step 1, we must have FFC data for the given sapcode in both appsFFCProductList & allVersionsFFCProductList
				 */
				if (dataFetched && (!AppsUtil.appsFFCProductList || !AppsUtil.appsFFCProductList[sapcode])) {
					if (!AppsUtil.appsFFCProductList) {
						AppsUtil.appsFFCProductList = {};
					}
					AppsUtil.appsFFCProductList[sapcode] = AppsUtil.allVersionsFFCProductList[sapcode][AppsUtil.allVersionsFFCProductList[sapcode].length - 1];
				}
				return dataFetched;
			});
		}
		/**
		 * Step - 2.1 - Now check for ffcFallbackProductsJson
		 * This will be needed only in the case of fallback scenario
		 * to form the dmg/ exe file name
		 */
		if (!AppsUtil.ffcFallbackProductsJson) {
			promiseArr[1] = RestApiUtil.fetchFFCFallbackProductsJson().then((ffcFallbackProductsData) => {
				if (ffcFallbackProductsData) {
					AppsUtil.processAndStoreFFCFallbackProducts(ffcFallbackProductsData);
					return true;
				}
				return false;
			});
		}
		// Step - 3 - In case of download via CCD, 'KCCC' data must also be present
		if (isDownloadViaCCD) {
			// Step - 3 - If we are downloading the given product via CCD, then we must also have the FFC data for KCCC
			const creativeCloudSapCode = 'KCCC';
			if (!AppsUtil.allVersionsFFCProductList[creativeCloudSapCode] || !(AppsUtil.appsFFCProductList && AppsUtil.appsFFCProductList[creativeCloudSapCode])) {
				let KCCCDataFetchPromise = Promise.resolve(false);
				if (AppsUtil.allVersionsFFCProductList[creativeCloudSapCode]) {
					KCCCDataFetchPromise = Promise.resolve(true);
				}
				else if (!AppsUtil.isFFCFallback) {
					KCCCDataFetchPromise = RestApiUtil.fetchFFCAllVersionData(creativeCloudSapCode);
				}
				else {
					KCCCDataFetchPromise = RestApiUtil.fetchFFCAllVersionDataFallback(creativeCloudSapCode);
				}
				promiseArr[2] = KCCCDataFetchPromise.then((dataFetched) => {
					if (dataFetched && (!AppsUtil.appsFFCProductList || !AppsUtil.appsFFCProductList[creativeCloudSapCode])) {
						if (!AppsUtil.appsFFCProductList) {
							AppsUtil.appsFFCProductList = {};
						}
						AppsUtil.appsFFCProductList[creativeCloudSapCode] = AppsUtil.allVersionsFFCProductList[creativeCloudSapCode][AppsUtil.allVersionsFFCProductList[creativeCloudSapCode].length - 1];
					}
					return dataFetched;
				});
			}
		}
		const response = await Promise.all(promiseArr);
		/**
		 * If fallback -> all pieces of promises must be resolved
		 * If not fallback -> only AppsUtil.appsFFCProductList and AppsUtil.allVersionsFFCProductList will suffice
		 */
		if (AppsUtil.isFFCFallback) {
			return response[0] && response[1] && response[2];
		}
		return response[0] && response[2];
	}


	startDownload(product: Object, isThorPresent: boolean, isAllApps: boolean, passPhraseFailureCb: Function, downloadCoachMark: React.Element) {
		if (isThorPresent) {
			this.downloadViaThor(product, isAllApps, passPhraseFailureCb);
		}
		else {
			this.downloadViaHiddenIframe(product, isAllApps);
			downloadCoachMark && CSSUtil.showDownloadCoachMark(downloadCoachMark);
		}
	}

	/**
	 * Opens up the download URL inside of a hidden iFrame in order to avoid
	 * interfering with other HTTP requests that may be occurring in the main
	 * body of the page (e.g. - learn content images, product icons, etc).
	 *
	 * @param url Required param.
	 */
	addIframe(url: string) {
		if (document.getElementById('download_frame')) {
			document.getElementById('download_frame').setAttribute('src', url);
		}
		else {
			const ifrm = document.createElement('iframe');
			ifrm.setAttribute('src', url);
			ifrm.setAttribute('id', 'download_frame');
			ifrm.setAttribute('frameborder', '0');
			ifrm.style.width = '0';
			ifrm.style.height = '0';
			ifrm.style.position = 'absolute';
			document.body.appendChild(ifrm);
		}
	}

	/**
	 * Returns the plancode array
	 *
	 * @returns plancode array
	 */
	getPlanCodeList() {
		const planCodeArr = [];
		const gqlProfile = AppsUtil.profile && AppsUtil.profile.gqlProfile;
		if (gqlProfile) {
			const paidSubscriptions = gqlProfile.account && gqlProfile.account.subscriptions && gqlProfile.account.subscriptions.paid || [];
			const freeSubscription = gqlProfile.account && gqlProfile.account.subscriptions && gqlProfile.account.subscriptions.free || [];
			paidSubscriptions.forEach((plan) => {planCodeArr.push(plan.planCode);});
			freeSubscription.forEach((plan) => {planCodeArr.push(plan.planCode);});
		}
		else {
			AppsUtil.planCode && planCodeArr.push(AppsUtil.planCode);
		}
		return planCodeArr;
	}

	/**
	 * Returns the context data to be passed in the WAM call
	 *
	 * @param productCode Required param.
	 * @param userId Required param.
	 * @param authId Required param.
	 * @returns contextData
	 */
	getWamContextData(productCode: string, userId: ?string, authId: ?string, secondarySapCode: ?string) {
		/* eslint-disable camelcase */
		const contextData = {};
		contextData.component = 'cc-home';
		const appsAnalytics = new AppsAnalytics(getConfigApps('env'));
		contextData.visitor_guid = appsAnalytics.getVisitorGuid();
		contextData.campaign_id = Url.getParam('campaign_id');

		if (platform) {
			contextData.browser = platform.name;
		}

		contextData.context_guid = AnalyticsUtil.contextGuid;
		if (AnalyticsUtil.contextParams) {
			contextData.context_params = AnalyticsUtil.contextParams;
		}

		if (AppsUtil.isLoggedOutPromotionExp) {
			if (Url.getParam('campaignKey')) {
				contextData.campaignKey = Url.getParam('campaignKey');
			}
			if (Url.getParam('workflow')) {
				contextData.workflow = Url.getParam('workflow');
			}
		}

		if (AppsUtil.isLoggedOutCampaignExp) {
			const campaignKey = Url.getParam('exp');
			if (campaignKey) {
				contextData.campaignKey = campaignKey;
			}
			if (Url.getParam('workflow')) {
				contextData.workflow = Url.getParam('workflow');
			}
			if (AppsUtil.hasFeature('temp_wam_2280') && this._shouldGiveKaizen2()) {
				// Nested JSONs work only for Kaizen 2.0
				contextData.deepLinkParams = {
					campaignKey,
					workflow: AppsUtil.convertHyphenatedToCamelCase(Url.getParam('workflow')),
					file: Url.getParam('file'),
					path: Url.getParam('path'),
					defaultPath: Url.getParam('defaultPath'),
					id: Url.getParam('id')
				};
			}
		}

		const contextDataObj = {};
		setExpCampaignAndVariation(contextDataObj);
		contextData.campaign_id = contextDataObj.setExpCampaignID;
		contextData.variation_id = contextDataObj.setExpVariationID;
		contextData.experience_id = contextDataObj.setExpExperienceID;

		// add plan code array
		if (AppsUtil.hasFeature('temp_pass_plan_code_to_kaizen')) {
			contextData.planCodeList = this.getPlanCodeList().join('|');
		}

		// pass Acrobat plan codes till the next Kaizen release
		if (AppsUtil.hasFeature('temp_cch_11632') && productCode === 'APRO') {
			contextData.detectAndLaunchPlanCodeList = 'apcc_direct_individual|apap_direct_individual|apro_direct_individual|acro_direct_individual|acrobat_pro_dc_sign_funnel_indiv_individual|acrobat_pro_dc_sign_funnel_team_team|acrobat_pro_dc_plus_sign_funnel_individual|acrobat_pro_dc_plus_sign_funnel_team|acrobat_pro_dc_with_advanced_e_sign_pdf_funnel_individual|acrobat_pro_dc_plus_pdf_funnel_team';
		}

		// add installerWaitTime
		if (AppsUtil.hasFeature('temp_pass_installerWaitTime_to_kaizen')) {
			contextData.installerWaitTime = 30;
		}

		if (AppsUtil.hasFeature('temp_cch_11843_11844')) {
			// add updateCCD to control whether Kaizen updates CCD or not (CCH-11843)
			if (productCode === 'KCCC') {
				contextData.updateCCD = 'true';
			}

			// add secondarySapcodeList for multiple app downloads (CCH-11844)
			// Pipe separated sapcodes that should be installed along with main sapcode. Eg: "LTRM|PPRO"
			contextData.secondarySapcodeList = '';
		}

		// CCH-11582
		if (isTestAExperience(campaignId.kaizenInstallerWaitTime)) {
			contextData.installerWaitTime = 45;
		}
		if (isTestBExperience(campaignId.kaizenInstallerWaitTime)) {
			contextData.installerWaitTime = 120;
		}

		if (productCode) {
			contextData.Product_ID_Promoted = productCode;
		}

		if (secondarySapCode) {
			contextData.secondarySapCode = secondarySapCode;
		}

		if (userId) {
			contextData.userGuid = userId;
		}

		if (authId) {
			contextData.authId = authId;
		}

		contextData.contextComName = getComName();
		contextData.contextSvcName = getSvcName();
		contextData.contextOrigin = getComName();

		if (Url.getParam('promoid')) {
			contextData.promocode = Url.getParam('promoid');
		}

		const addCookieToContext = (cookieName) => {
			const cookie = getCookie(cookieName);
			if (cookie) {
				contextData[cookieName] = cookie;
			}
		};

		addCookieToContext('aam_uuid');
		addCookieToContext('gpv');
		addCookieToContext('s_vi');
		addCookieToContext('creative-cloud-referrer');

		// special handling for cookie AMCV_<variable>
		const cookieNameStartPos = document.cookie.search('AMCV_');
		if (cookieNameStartPos !== -1) {
			const tempAMCVBuffer = document.cookie.substring(cookieNameStartPos);
			if (tempAMCVBuffer) {
				const cookieNameEndPos = tempAMCVBuffer.search('=');
				if (cookieNameEndPos !== -1) {
					const op = tempAMCVBuffer.substring(0, cookieNameEndPos);
					addCookieToContext(op);
				}
			}
		}

		if (window.s_adbadobenonacdc && window.s_adbadobenonacdc.visitor) {
			contextData.mid = window.s_adbadobenonacdc.visitor.getMarketingCloudVisitorID();
			contextData.aid = window.s_adbadobenonacdc.visitor.getAnalyticsVisitorID();
			contextData.AppMeasurementVersion = window.s_adbadobenonacdc.version;
		}

		// For OFT: https://wiki.corp.adobe.com/x/gxShQQ
		contextData.kaizenTrialDuration = window.kaizenTrialDuration ? window.kaizenTrialDuration : 7;

		return contextData;
		/* eslint-enable camelcase */
	}

	_shouldGiveKaizen2() {
		// kaizen 2.0 not supported for MacOS <= 10.11 and Win < 10
		let isKaizen2Incompatible;
		if (DeviceDetectionUtil.isWin()) {
			isKaizen2Incompatible = DeviceDetectionUtil.isWin7or8();
		}
		else if (DeviceDetectionUtil.isMacOS()) {
			const macOS = DeviceDetectionUtil.getMacVersion();
			if (macOS && macOS !== null) {
				const macOSArr = macOS.split('.');
				isKaizen2Incompatible = (macOSArr[0] === '10' && macOSArr[1] && macOSArr[1] === '11')
					|| (parseInt(macOSArr[0], 10) < 10 || (macOSArr[0] === '10' && macOSArr[1] && parseInt(macOSArr[1], 10) < 11));
			}
		}
		else {
			isKaizen2Incompatible = (DeviceDetectionUtil.isLinux() || DeviceDetectionUtil.isChromeOS());
		}

		return !isKaizen2Incompatible;
	}

	/**
	 * Returns the WAM download URL for a given sap code
	 *
	 * @param sapCode Required param.
	 * @param name Required param.
	 * @param version Required param.
	 * @returns url
	 */
	getDownloadWAMUrl(sapCode: string, name: string, version: ?string, secondarySapCode?: ?string) {
		let userId = null;
		let authId = null;

		if (window.adobeIMS && window.adobeIMS.isSignedInUser && window.adobeIMS.isSignedInUser() && window.adobeIMS.getUserProfile) {
			userId = (adobeIMS.getUserProfile() && adobeIMS.getUserProfile().userId) || userId;
			authId = (adobeIMS.getUserProfile() && adobeIMS.getUserProfile().authId) || authId;
		}

		const contextData = this.getWamContextData(sapCode, userId, authId, secondarySapCode);
		const servers = getConfigApps('servers');
		let url;
		if (!AppsUtil.isFFCFallback) {
			url = servers.WAM;
		}
		else {
			const {productName} = AppsUtil.ffcFallbackProductsJson && AppsUtil.ffcFallbackProductsJson[sapCode];
			url = servers.WAM_fallback;
			url = `${url}/${DeviceDetectionUtil.isWin() ? 'win' : 'mac'}/${productName.replace(/ /g, '_')}${DeviceDetectionUtil.isWin() ? '_Set-Up.exe' : '_Installer.dmg'}`;
			return url;
		}

		url = `${url}?sapCode=${sapCode}&productName=${name.replace(' CC', '')}${version ? `&version=${version}` : ''}&os=${DeviceDetectionUtil.isWin() ? 'win' : 'mac'}&guid=${uuid.v4()}`;
		url = `${url}&contextParams=${encodeURIComponent(JSON.stringify(contextData))}`;

		/**
		 * As discussed with Kaizen team, the precendence will be as follows:
		 * 1. query param wamFeature
		 * 2. if NUJ test live, download Kaizen 2.0
		 */
		const wamFeatureVal = Url.getParam('wamFeature');
		if (wamFeatureVal) {
			url = `${url}&wamFeature=${wamFeatureVal}`;
		}
		else if (this._shouldGiveKaizen2()) {
			url = `${url}&wamFeature=nuj-live`;
		}
		url = `${url}&environment=${getConfigApps('env')}`;

		// Instead of passing x-api-key in the header, passing as a query parameter.
		url = `${url}&api_key=${getConfigApps('cchome').clientId}`;

		return url;
	}

	/**
	 * Checks if the given url is safe
	 *
	 * @param url Required param.
	 * @param allowList Required param.
	 */
	checkInputUrlValidity(url, allowList) {
		const splitUrl = url.split('/');

		// Redirect to or open a file from a https url only
		if (splitUrl[0] !== 'https:') {
			return false;
		}
		if (splitUrl[1] !== '') {
			return false;
		}
		if (!splitUrl[2]) {
			return false;
		}

		const domainSplitArr = splitUrl[2].split('.');
		const len = domainSplitArr.length;
		if (len < 2) {
			return false;
		}
		const domain = `${domainSplitArr[len - 2]}.${domainSplitArr[len - 1]}`;

		// Domain name should be part of allowList
		return allowList.indexOf(domain) > -1;
	}

	/**
	 * Downloads the given file via a hidden iframe
	 *
	 * @param filePath Required param.
	 */
	downloadFile(filePath: string) {
		if (this.checkInputUrlValidity(filePath, ['adobelanding.com', 'adobe.com'])) {
			this.addIframe(filePath);
			return true;
		}
		return false;
	}

	/**
	 * Validates default path URL in route-to-path workflow
	 * of logged out campaign Pages
	 *
	 * @param path Required param.
	 */
	validateDefaultPathUrl(path: string) {
		return this.checkInputUrlValidity(path, ['adobelanding.com', 'adobe.com', 'behance.net', 'acrobat.com']);
	}

	/**
	 * Downloads the given product via a hidden iframe
	 *
	 * @param product Required param.
	 */
	downloadViaHiddenIframe(product: Object, isAllApps: boolean) {
		let url;
		// let workflow;

		if (isAllApps) {
			url = this.getDownloadWAMUrl('KCCC', 'Creative Cloud', null, product.id === 'KCCC' ? null : product.id);
			this.addIframe(url); // load LBS installer link in iframe
			// workflow = 'thor';
		}
		else {
			url = this.getDownloadWAMUrl(product.id, product.displayName, product.version);
			this.addIframe(url);
		}

		// hide installation toast and banner once thor has been downloaded.
		AppsUtil.hideCCInstallationToast !== null && AppsUtil.hideCCInstallationToast();
		AppsUtil.sessionStorageSet('showCCInstallationBanner', 'false');
		AppsUtil.sessionStorageSet('showCCInstallationToast', 'false');
		AppsUtil.hideCCInstalationBanner !== null && AppsUtil.hideCCInstalationBanner();
	}

	/**
	 * Downloads the given product via Thor
	 *
	 * @param product Required param.
	 */
	downloadViaThor(product: Object, isAllApps: boolean, failureCallback: Function) {
		const platformObject = AppsUtil.getPlatformObject(product.platforms.platform);

		/* If we can get the version of AAM plugin it means this is a version of AAM that supports downloading multiple products
		 and we should use the Adobe Creative Cloud protocol which is returned by FFC on the cc-uri element.
		 https://wiki.corp.adobe.com/display/Suites/Requirements+from+the+Web+Team

		 If the AAM version supports cc-uri, we should try to get protocol based on version '3.0.0.0', otherwise we should get protocol based on version 'default'.
		 */
		let searchVersion;
		if (ThorConnectionUtil.aamPluginSupportsCCURI()) {
			searchVersion = '3.0.0.0'; // this version is only for use with newer Thor
		}
		else {
			searchVersion = '2.0.0.0'; // This URI can be used with older Thor
		}

		// as per FFC's request, use URLs for en_US by default
		// https://git.corp.adobe.com/Nitro-CCM-Team/ccweb/issues/1624
		const userLocale = FFCLocaleMap.en || 'en_US';

		let url = '';
		if (platformObject) {
			// as per FFC's request, use URLs for en_US by default
			// https://git.corp.adobe.com/Nitro-CCM-Team/ccweb/issues/1624
			let languageSet = AppsUtil.getLanguageSet(platformObject, userLocale);
			languageSet = languageSet ? languageSet : platformObject.languageSet[0];
			url = languageSet.urls['cc-uri'].uri.find(uriObject => uriObject.version === searchVersion).value;
			if (!url) {
				// If we looked for a specific version but didn't find it, try getting default before punting to the aamURL
				url = languageSet.urls['cc-uri'].uri.find(uriObject => uriObject.version === 'default').value;
				if (!url) {
					// no cc-uri configuration of Adobe Creative Cloud protocol was available, use the old param location
					url = `${languageSet.urls.aamURL}?productVersion=${encodeURIComponent(product.version)}`;
				}
			}
			if (!url && failureCallback) {
				failureCallback('url_not_available');
				return;
			}
		}

		// TODO: Error screen for URL determination fail and Passphrase fail not determined

		// Use ? as param separator per AAM spec at
		// https://zerowing.corp.adobe.com/display/Suites/Thor+URI+Handler
		let paramPrefix = '?';

		if (ThorConnectionUtil.aamPluginSupportsAmpersand() && url.indexOf('?') >= 0) {
			paramPrefix = '&';
		}

		url = `${url}${paramPrefix}checkPrevious=true${paramPrefix}productName=${AppsUtil.getProductDisplayName(AppsUtil.getProductIdFromSapcode(product.id))}`;

		if (AppPickerUtil.isNUJAppPickerExperience() || AppPickerUtil.isSingleAppPlanNUJExp()) {
			url = `${url}${paramPrefix}forceRefresh=true`;
		}

		url = `${url}${paramPrefix}context_com_name=${encodeURIComponent(getComName())}${paramPrefix}context_svc_name=${encodeURIComponent(getSvcName())}${paramPrefix}context_origin=${encodeURIComponent(getComName())}`;
		if (window.adobeIMS
			&& window.adobeIMS.isSignedInUser
			&& window.adobeIMS.isSignedInUser()
			&& window.adobeIMS.getUserProfile) {
			if (adobeIMS.getUserProfile().userId) {
				url = `${url}${paramPrefix}context_user_guid=${encodeURIComponent(adobeIMS.getUserProfile().userId)}`;
			}
			if (adobeIMS.getUserProfile().authId) {
				url = `${url}${paramPrefix}context_user_auth_id=${encodeURIComponent(adobeIMS.getUserProfile().authId)}`;
			}
		}


		if (isAllApps || AppsUtil.isLoggedOutCampaignExp) {
			if (ThorConnectionUtil.aamPluginSupportsAmpersand()) {
				url = url.replace(/version=.*&/, '');
			}
			else {
				url = url.replace(/version=([^?]*)+\?/, '');
			}
		}

		url = url.replace(/\/$/, '');

		const campaignKey = Url.getParam('exp') || '';
		if (AppsUtil.isLoggedOutPromotionExp) {
			url = `${url}${paramPrefix}workflow=${Url.getParam('workflow')}${paramPrefix}campaignKey=${campaignKey}`;
		}
		else if (AppsUtil.isLoggedOutCampaignExp && campaignKey) {
			url = `${url}${paramPrefix}campaignKey=${campaignKey}`;
		}

		url = `${url}${paramPrefix}context_guid=${encodeURIComponent(AnalyticsUtil.contextGuid)}`;
		if (AnalyticsUtil.contextParams) {
			url = `${url}${paramPrefix}context_params=${encodeURIComponent(AnalyticsUtil.contextParams)}`;
		}

		AppsUtil.printLog(url);
		wamClient.launchCCDUI({env: window.ENV, aamUrl: url, paramPrefix, failureCallback});
	}

	routeToCCDPath(routePath: String) {
		const url = `aam://ccd?workflow=routeToPath&routePath=${routePath}`;

		AppsUtil.printLog(url);
		wamClient.launchCCDUI({env: window.ENV, aamUrl: url, paramPrefix:'&'});
	}

	getValidUriFromUrl(routePath: String) {
		const uriMappingObj = campaignUrlUriMapping.find(item => item.urlId === routePath);
		return uriMappingObj && uriMappingObj.uri;
	}

	routeToProductPath(routePath: String) {
		const uriHandlerPath = `${this.getValidUriFromUrl(routePath)}&context_guid=${encodeURIComponent(AnalyticsUtil.contextGuid)}&context_params=${encodeURIComponent(AnalyticsUtil.contextParams)}`;
		AppsUtil.printLog(uriHandlerPath);
		window.open(uriHandlerPath, '_parent');
	}

	getDownloadFileName(sapcode: string) {
		const productName = AppsUtil.isFFCFallback
			? AppsUtil.ffcFallbackProductsJson[sapcode].productName
			: AppsUtil.getFFCProductConfigBySapCode(sapcode).displayName;
		return `${productName.replace(/ CC/g, '').replace(/ /g, '_')}${(AppsUtil.isWin() ? '_Set-Up.exe' : '_Installer.dmg')}`;
	}

	getEmailMeLinkDownloadUrl(isCreativeCloud: boolean, process: string, inputDownloadURL: string, guid: string, appId: ?string) {
		if (process === 'ccd') {
			return inputDownloadURL;
		}
		if (isCreativeCloud || appId === 'creative-cloud') {
			return `https://www.adobe.com/go/cchome_dnld_creativecloud_${getConfigApps('env')}?x-product=CCHome&x-product-location=Apps&guid=${guid}&source=email`;
		}
		if (AppsUtil.hasFeature('temp_cch_8676')) {
			return appId ? `${`https://www.adobe.com/go/cchome_dnld_${appId}_${getConfigApps('env')}`}?x-product=CCHome&x-product-location=Apps&guid=${guid}&source=email&context_guid=${encodeURIComponent(AnalyticsUtil.contextGuid)}&context_params=${encodeURIComponent(AnalyticsUtil.contextParams)}` : '';
		}
		return appId ? `${`https://www.adobe.com/go/cchome_dnld_${appId}_${getConfigApps('env')}`}?x-product=CCHome&x-product-location=Apps&guid=${guid}&source=email` : '';
	}

	getEmailMeLinkHref(isCreativeCloud: boolean, productName: string, appId: ?string, process: string, inputDownloadURL: string, guid: string, formatMessage: Function) {
		const appname = isCreativeCloud ? formatMessage({id: 'apps.creativeCloud.displayName'}) : productName;
		const emailSubject = formatMessage({id: 'apps.email.productEmailSubject'}, {appname});
		const emailBody = formatMessage({id: 'apps.email.productEmailBody'}, {appname, downloadurl: this.getEmailMeLinkDownloadUrl(isCreativeCloud, process, inputDownloadURL, guid, appId)});
		return `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
	}

	async getThorStatus(currentThorStatus: ?Object) {
		if (typeof currentThorStatus === 'boolean') {
			return currentThorStatus;
		}
		AppsUtil.hasThor = await ThorConnectionUtil.checkForThor();
		return AppsUtil.hasThor;
	}
}

export default new DownloadUtil();
